/**
 * @file Shared board-table cell primitives.
 *
 * Domain table adapters own business-specific cell value formatting. This
 * module owns reusable table cell snippets used across parent-board tables.
 */

import {
    escapeHTML,
} from "../utils/contentUtils.js";
import {
    isColorDark,
} from "../utils/displayUtils.js";

/**
 * Renders a small appended state icon for table identity cells.
 * @param {object} params Icon params.
 * @param {string} params.icon Material Symbol icon name.
 * @param {string} params.label Accessible label and tooltip.
 * @param {string} [params.className] Additional classes.
 * @returns {string} State icon HTML.
 */
export function renderAppendedStateIcon({
  icon,
  label,
  className = "",
}) {
  if (!icon || !label) {return "";}
  return `
    <span
      class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 ${className}"
      title="${escapeHTML(label)}"
      aria-label="${escapeHTML(label)}"
    >
      <span class="material-symbols-outlined text-[15px] leading-none" aria-hidden="true">${escapeHTML(icon)}</span>
    </span>
  `;
}

/**
 * Renders the recurring-task appended table icon.
 * @param {object} task Task record.
 * @returns {string} Recurring indicator HTML.
 */
export function renderTaskRecurringTableIcon(task) {
  return task?.recurrence?.frequency ?
    renderAppendedStateIcon({
      icon: "event_repeat",
      label: "Recurring task",
      className: "text-green-600",
    }) :
    "";
}

/**
 * Renders configured type badges for a board-table cell.
 * @param {Array<string>} typeIds - Selected type ids.
 * @param {Array<object>} typeConfig - Type configuration entries.
 * @returns {string} Type badge HTML.
 */
export function renderBoardTableTypeBadges(typeIds, typeConfig) {
  const safeTypeIds = Array.isArray(typeIds) ? typeIds : [];

  if (safeTypeIds.length === 0) {
    return "<span class=\"text-xs text-gray-400\">None</span>";
  }

  const sortedTypeIds = [...safeTypeIds].sort((a, b) => {
    const typeA = typeConfig.find((entry) => entry.id === a);
    const typeB = typeConfig.find((entry) => entry.id === b);
    const orderA = typeA?.order ?? 999;
    const orderB = typeB?.order ?? 999;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (typeA?.name || a).localeCompare(typeB?.name || b);
  });

  return `<div class="flex flex-wrap gap-1">${sortedTypeIds.map((typeId) => {
    const typeOption = typeConfig.find((entry) => entry.id === typeId);
    const badgeName = typeOption?.name || "Unknown Type";
    const badgeColor = typeOption?.color || "#d1d5db";
    const textColor = isColorDark(badgeColor) ? "text-white" : "text-gray-800";
    return `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${badgeColor};">${escapeHTML(badgeName)}</span>`;
  }).join("")}</div>`;
}

/**
 * Renders the quick-add task row action cell.
 * @param {string} itemType - Parent item type.
 * @param {string} itemId - Parent item id.
 * @returns {string} Quick-add table cell HTML.
 */
export function renderQuickAddTaskCell(itemType, itemId) {
  const btnDataset = `data-${itemType}-id="${itemId}"`;

  return `<td class="sticky right-0 z-[1] w-12 bg-inherit px-2 py-4 text-center">
                        <button type="button" class="quick-add-task-btn table-action-btn" ${btnDataset} title="Add Task">
                             <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" /></svg>
                        </button>
                    </td>`;
}

/**
 * Renders the task quick-close row action cell.
 * @param {string} taskId - Task id.
 * @returns {string} Quick-close table cell HTML.
 */
export function renderTaskQuickCloseCell(taskId) {
  return `<td class="sticky right-0 z-[1] w-12 bg-inherit px-2 py-4 text-center">
                    <button type="button" class="task-quick-close-btn icon-action-btn table-action-btn" data-task-id="${taskId}" title="Quick close">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </button>
                </td>`;
}
